import { useMemo } from 'react';
import { useResource } from '@axios-use/react';
import pick from 'lodash/pick';
import type { PostMcProductDetailQuery, GetCouponProductListQuery } from '@/apis/product';
import { getProductDescReqConfig, getPOSTMcProductDetailReqConfig, getCouponProductList } from '@/apis/product';
import type { ProductData, ProductInfo } from '@/apis/product/types';
import { isDCSingleCombo } from './func';

/**
 * [patch 20250228] DC type=7 单选(single)&非包含(isIncludeRound=0)商品，quantity,isDefault 冲突数据处理
 */
function patchDCProductData(data?: ProductData): ProductData | undefined {
  const _formatDCProduct = (p: ProductInfo): ProductInfo => {
    if (
      p?.type === 7 &&
      p.comboItems &&
      p.comboItems.some((c) => Number(c.isIncludeRound) !== 1 && isDCSingleCombo(c))
    ) {
      return {
        ...p,
        comboItems: p.comboItems.map((c) => {
          if (
            Number(c.isIncludeRound) !== 1 &&
            isDCSingleCombo(c) &&
            c.comboProducts?.some((cp) => cp.quantity === 1)
          ) {
            return {
              ...c,
              comboProducts: c.comboProducts.map((cp) => {
                if (cp.quantity !== 1 && cp.isDefault === 1) {
                  return { ...cp, isDefault: 0 };
                }
                return cp;
              }),
            };
          }
          return c;
        }),
      };
    }
    return p;
  };

  if (data?.product?.type === 7) {
    return {
      ...data,
      product: _formatDCProduct(data.product),
    };
  } else if (data?.product?.type === 3) {
    return {
      ...data,
      product: {
        ...data.product,
        products: data.product.products?.map((p) => _formatDCProduct(p)),
      },
    };
  }

  return data;
}

export default function useProductDetail(
  params?: Partial<PostMcProductDetailQuery>,
  needCouponPrice?: boolean,
  options?: {
    onCompleted?: (response: any) => void;
  },
) {
  const { isGroupMeal, beType } = params || {};
  const [proRes, refreshProduct] = useResource(getPOSTMcProductDetailReqConfig, [params as PostMcProductDetailQuery], {
    filter: (p) => Boolean(p && p.productCode && p.storeCode && p.daypartCode && p.orderType),
    onCompleted(data, response: any) {
      options?.onCompleted?.(response);
    },
  });
  const productData = useMemo(() => patchDCProductData(proRes.data), [proRes.data]);

  const [descRes, refreshProductDesc] = useResource(getProductDescReqConfig, [{ isGroupMeal, beType }]);
  const [couponsProductsRes] = useResource(
    getCouponProductList,
    [
      {
        ...pick(params, [
          'daypartCode',
          'storeCode',
          'orderType',
          'couponCode',
          'couponId',
          'promotionId',
          'beCode',
          'date',
          'time',
          'isGroupMeal',
          'beType',
        ]),
      } as unknown as GetCouponProductListQuery,
    ],
    {
      filter: (p) =>
        Boolean(needCouponPrice && p.daypartCode && p.storeCode && p.orderType && p.couponCode && p.couponId),
      cache: false,
    },
  );
  const couponPrice = useMemo(() => {
    const _productCode = productData?.product.code;
    if (couponsProductsRes.data?.productList && _productCode) {
      const _p = couponsProductsRes.data.productList.find((p) => p.productCode === _productCode);
      return _p?.priceInfo;
    }
    return undefined;
  }, [couponsProductsRes.data?.productList, productData?.product.code]);

  return {
    productData,
    productLoading: proRes.isLoading,
    refreshProduct,
    productDesc: descRes.data,
    productDescLoading: descRes.isLoading,
    refreshProductDesc,
    couponPrice,
  };
}
